import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { C } from "@/theme";
import { useAuth } from "@/auth";
import { Card, Checkbox, Field, OptionSelector, Picker, FormScreen } from "@/ui";

export default function TrainerCheckRide() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const id = params.get("id");
  const editing = !!id;
  const trainersQ = useQuery({ queryKey: ["trainers"], queryFn: () => api.get("/trainers") });
  const subjects = (trainersQ.data ?? []).filter((t) => t.active && t.role === "Tutor");
  const myId = (trainersQ.data ?? []).find((t) => t.name === user?.name)?.id ?? "";
  const listQ = useQuery({ queryKey: ["trainer-checkrides"], queryFn: () => api.get("/trainer-checkrides"), enabled: editing });

  const [subjectId, setSubjectId] = useState("");
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [target, setTarget] = useState("");
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [attentionReason, setAttentionReason] = useState("");
  const [withAssociate, setWithAssociate] = useState(false);
  const [cr, setCr] = useState({ briefing_done: false, maintained_contact: false, debrief_done: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (editing && !loaded && listQ.data) {
      const f = listQ.data.find((c) => c.id === id);
      if (f) {
        setSubjectId(f.subject_trainer_id);
        setDate(f.date?.slice(0, 10) ?? "");
        setReason(f.reason ?? "");
        setTarget(f.target_plan ?? "");
        setResult(f.result ?? "");
        setNotes(f.notes ?? "");
        setOutcomeNote(f.outcome_note ?? "");
        setAttentionReason(f.attention_reason ?? "");
        setWithAssociate(!!f.with_associate);
        setCr({ briefing_done: !!f.briefing_done, maintained_contact: !!f.maintained_contact, debrief_done: !!f.debrief_done });
        setLoaded(true);
      }
    }
  }, [editing, loaded, listQ.data, id]);

  const attentionRequired = result === "Requires Attention";
  const save = useMutation({
    mutationFn: () => {
      const payload = { subject_trainer_id: subjectId, assessor_id: myId, date: date || undefined, reason, target_plan: target, with_associate: withAssociate, briefing_done: cr.briefing_done, maintained_contact: cr.maintained_contact, debrief_done: cr.debrief_done, result, notes, outcome_note: outcomeNote, attention_reason: attentionReason, actor_id: user?.id ?? "", actor_role: user?.role ?? "" };
      return editing ? api.put(`/trainer-checkrides/${id}`, payload) : api.post("/trainer-checkrides", payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainer-checkrides"] }); navigate(-1); },
  });
  const disabled = !subjectId || !result || (attentionRequired && !attentionReason.trim());

  return (
    <FormScreen title={editing ? "Edit Tutor Check Ride" : "Tutor Check Ride"} saveLabel={editing ? "Save" : "Record"} onClose={() => navigate(-1)} onSave={() => save.mutate()} saving={save.isPending} saveDisabled={disabled}>
      <Picker label="Tutor being assessed" testID="tcr-trainer-picker" placeholder="Select a trainer" searchable value={subjectId} onSelect={setSubjectId} options={subjects.map((t) => ({ key: t.id, label: t.name, sub: t.is_assessor ? "Assessor-Tutor" : "Tutor" }))} />
      {subjects.length === 0 ? <p style={{ color: C.muted }} className="text-[13px] italic">No active tutors to assess yet.</p> : null}
      <Field label="Date" value={date} onChange={setDate} type="date" testID="tcr-date" />
      <Card>
        <p style={{ color: C.onSurface }} className="font-extrabold mb-2">Assessor Plan</p>
        <div className="flex flex-col gap-3"><Field label="Reason for Check Ride" value={reason} onChange={setReason} multiline testID="tcr-reason" /><Field label="Target Plan for Ride" value={target} onChange={setTarget} multiline testID="tcr-target" /></div>
        <div className="mt-2">
          <Checkbox label="Check Ride with Associate" checked={withAssociate} onChange={() => setWithAssociate((v) => !v)} testID="tcr-with-associate" />
          {[["briefing_done", "Briefing given"], ["maintained_contact", "Maintained contact"], ["debrief_done", "Debrief conducted"]].map(([key, label]) => (
            <Checkbox key={key} label={label} checked={cr[key]} onChange={() => setCr((p) => ({ ...p, [key]: !p[key] }))} testID={`tcr-${key}`} />
          ))}
        </div>
      </Card>
      <Field label="Notes / Ride Details" value={notes} onChange={setNotes} multiline testID="tcr-notes" />
      <OptionSelector label="Result" options={["Approved", "Requires Attention"]} value={result} onChange={setResult} testID="tcr-result" />
      {attentionRequired ? (
        <>
          <Field label="Reason for attention (required)" value={attentionReason} onChange={setAttentionReason} multiline placeholder="Why does this tutor require attention?" testID="tcr-attention-reason" />
          {!attentionReason.trim() ? <p style={{ color: C.error }} className="text-[13px] font-semibold -mt-2">A reason is required when the result is Requires Attention.</p> : null}
        </>
      ) : null}
      <Field label="Outcome note for Tutor (one line)" value={outcomeNote} onChange={setOutcomeNote} placeholder="Short summary the tutor will see" testID="tcr-outcome-note" />
      <p style={{ color: C.muted }} className="text-[13px] italic">This check ride is recorded against the tutor and does not affect any rider pathway.</p>
    </FormScreen>
  );
}
